import React, { useEffect, useState } from 'react';
import { Plus, Save } from 'lucide-react';
import { settingsApi } from '../api/settings';
import { useToast } from '../hooks/useToast';

const Settings: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab]             = useState<'company'|'branches'|'locations'|'users'>('company');
  const [company, setCompany]     = useState<any>(null);
  const [branches, setBranches]   = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [users, setUsers]         = useState<any[]>([]);
  const [roles, setRoles]         = useState<any[]>([]);
  const [saving, setSaving]       = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [branchForm, setBranchForm]   = useState({ name:'', code:'', address:'', phone:'', email:'' });
  const [locationForm, setLocationForm] = useState({ name:'', branch_id:'' });
  const [userForm, setUserForm]   = useState({ name:'', email:'', password:'', role_id:'', scope:'branch' });

  const load = () => {
    settingsApi.getCompany().then(r => setCompany(r.data.data));
    settingsApi.getBranches().then(r => setBranches(r.data.data));
    settingsApi.getLocations().then(r => setLocations(r.data.data));
    settingsApi.getUsers().then(r => setUsers(r.data.data));
    settingsApi.getRoles().then(r => setRoles(r.data.data));
  };

  useEffect(() => { load(); }, []);

  const handleUpdateCompany = async () => {
    setSaving(true);
    try {
      await settingsApi.updateCompany({ name:company.name, email:company.email, phone:company.phone, address:company.address, tax_number:company.tax_number, timezone:company.timezone });
      showToast('Company updated!');
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleBranch = async () => {
    if (!branchForm.name || !branchForm.code) { showToast('Name and code required', 'error'); return; }
    try {
      await settingsApi.createBranch(branchForm);
      showToast('Branch created!'); setShowForm(false); setBranchForm({ name:'', code:'', address:'', phone:'', email:'' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleLocation = async () => {
    if (!locationForm.name || !locationForm.branch_id) { showToast('Name and branch required', 'error'); return; }
    try {
      await settingsApi.createLocation({ ...locationForm, branch_id: Number(locationForm.branch_id) });
      showToast('Location created!'); setShowForm(false); setLocationForm({ name:'', branch_id:'' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password || !userForm.role_id) { showToast('All fields required', 'error'); return; }
    try {
      await settingsApi.createUser({ ...userForm, role_id: Number(userForm.role_id) });
      showToast('User created!'); setShowForm(false); setUserForm({ name:'', email:'', password:'', role_id:'', scope:'branch' }); load();
    } catch (err:any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const tabs = [
    { key:'company',   label:'Company' },
    { key:'branches',  label:'Branches' },
    { key:'locations', label:'Locations' },
    { key:'users',     label:'Users' },
  ];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        {tab !== 'company' && <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add {tab === 'branches' ? 'Branch' : tab === 'locations' ? 'Location' : 'User'}</button>}
      </div>

      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:'2px solid var(--bdr)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key as any); setShowForm(false); }} style={{ padding:'8px 16px', fontSize:12, fontWeight:600, border:'none', background:'none', cursor:'pointer', borderBottom: tab===t.key ? '2px solid var(--blue)' : '2px solid transparent', color: tab===t.key ? 'var(--blue)' : 'var(--tx3)', marginBottom:'-2px' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'company' && company && (
        <div className="card">
          <div className="card-header"><span>Company Information</span></div>
          <div className="card-body">
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Company Name *</label><input className="form-control" value={company.name||''} onChange={e => setCompany((c:any)=>({...c,name:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Tax Number</label><input className="form-control" value={company.tax_number||''} onChange={e => setCompany((c:any)=>({...c,tax_number:e.target.value}))} /></div>
            </div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={company.email||''} onChange={e => setCompany((c:any)=>({...c,email:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={company.phone||''} onChange={e => setCompany((c:any)=>({...c,phone:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Address</label><textarea className="form-control" rows={3} value={company.address||''} onChange={e => setCompany((c:any)=>({...c,address:e.target.value}))} /></div>
            <div className="form-grid-2">
              <div className="form-group"><label className="form-label">Timezone</label>
                <select className="form-control" value={company.timezone||'UTC'} onChange={e => setCompany((c:any)=>({...c,timezone:e.target.value}))}>
                  <option value="UTC">UTC</option>
                  <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                  <option value="Africa/Mogadishu">Africa/Mogadishu</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Plan</label><input className="form-control" value={company.plan||''} disabled style={{background:'var(--sur2)',color:'var(--tx3)'}} /></div>
            </div>
            <button className="btn btn-primary" onClick={handleUpdateCompany} disabled={saving}><Save size={13}/> {saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      )}

      {tab === 'branches' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New Branch</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-3">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={branchForm.name} onChange={e => setBranchForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Bosaso Branch" /></div>
                  <div className="form-group"><label className="form-label">Code *</label><input className="form-control mono" value={branchForm.code} onChange={e => setBranchForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="e.g. BSO" /></div>
                  <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={branchForm.phone} onChange={e => setBranchForm(f=>({...f,phone:e.target.value}))} /></div>
                </div>
                <div className="form-group"><label className="form-label">Address</label><input className="form-control" value={branchForm.address} onChange={e => setBranchForm(f=>({...f,address:e.target.value}))} /></div>
                <button className="btn btn-primary" onClick={handleBranch}>Save Branch</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Code</th><th>Phone</th><th>Address</th><th>Status</th></tr></thead>
              <tbody>
                {branches.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No branches yet.</p></div></td></tr>
                : branches.map(b => (
                  <tr key={b.id}>
                    <td style={{fontWeight:600}}>{b.name}</td>
                    <td className="td-code">{b.code}</td>
                    <td style={{color:'var(--tx3)'}}>{b.phone || '—'}</td>
                    <td style={{color:'var(--tx3)',fontSize:11}}>{b.address || '—'}</td>
                    <td><span className={`badge ${b.is_active ? 'badge-green' : 'badge-gray'}`}>{b.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'locations' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New Location</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-2">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={locationForm.name} onChange={e => setLocationForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Main Warehouse" /></div>
                  <div className="form-group"><label className="form-label">Branch *</label>
                    <select className="form-control" value={locationForm.branch_id} onChange={e => setLocationForm(f=>({...f,branch_id:e.target.value}))}>
                      <option value="">Select branch</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleLocation}>Save Location</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Branch</th><th>Status</th></tr></thead>
              <tbody>
                {locations.length === 0 ? <tr><td colSpan={3}><div className="empty-state"><p>No locations yet.</p></div></td></tr>
                : locations.map(l => (
                  <tr key={l.id}>
                    <td style={{fontWeight:600}}>{l.name}</td>
                    <td style={{color:'var(--tx3)'}}>{l.branch_name}</td>
                    <td><span className={`badge ${l.is_active ? 'badge-green' : 'badge-gray'}`}>{l.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'users' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom:12 }}>
              <div className="card-header"><span>New User</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
              <div className="card-body">
                <div className="form-grid-2">
                  <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={userForm.name} onChange={e => setUserForm(f=>({...f,name:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">Email *</label><input className="form-control" type="email" value={userForm.email} onChange={e => setUserForm(f=>({...f,email:e.target.value}))} /></div>
                </div>
                <div className="form-grid-3">
                  <div className="form-group"><label className="form-label">Password *</label><input className="form-control" type="password" value={userForm.password} onChange={e => setUserForm(f=>({...f,password:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">Role *</label>
                    <select className="form-control" value={userForm.role_id} onChange={e => setUserForm(f=>({...f,role_id:e.target.value}))}>
                      <option value="">Select role</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Scope</label>
                    <select className="form-control" value={userForm.scope} onChange={e => setUserForm(f=>({...f,scope:e.target.value}))}>
                      <option value="company">Company (all branches)</option>
                      <option value="branch">Branch (single branch)</option>
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleUser}>Create User</button>
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Scope</th><th>Status</th></tr></thead>
              <tbody>
                {users.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No users yet.</p></div></td></tr>
                : users.map(u => (
                  <tr key={u.id}>
                    <td style={{fontWeight:600}}>{u.name}</td>
                    <td style={{color:'var(--tx3)'}}>{u.email}</td>
                    <td><span className="badge badge-blue">{u.role_name}</span></td>
                    <td><span className="badge badge-purple">{u.scope}</span></td>
                    <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default Settings;
